import type { ArticleDoc, Block, Run } from '../wiki/types';
import { tokenize, type Tok } from './tokenize';
import {
  F,
  type Coin,
  type Decor,
  type EnemySpawn,
  type Level,
  type LinkInfo,
  type Occurrence,
  type Panel,
  type Section,
  type Tile,
  type TileKind,
} from './types';

export const SANS = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
export const SERIF = '"Linux Libertine", "Linux Libertine O", Georgia, "Times New Roman", Times, serif';

/** World-space layout metrics (px at zoom 1). The player is 12×18 and jumps ~76px. */
export const LAYOUT = {
  pageW: 842,
  left: 54,
  right: 826,
  wallR: 834,
  ladderX: 12,
  ladderW: 30,
  ledgeX: 2,
  ledgeEnd: 52,
  paraGap: 26,
  hole: 34,
  minGap: 24,
  indent: 30,
} as const;

export type Measure = (text: string, font: string) => number;

interface TextStyle {
  size: number;
  brick: number;
  base: number;
  pitch: number;
  family: string;
  weight?: number;
  italic?: boolean;
  color?: string;
  kind: TileKind;
  /** Apply word physics (springs, ice, one-way, timed) from typography. */
  physics: boolean;
}

const S = {
  body: { size: 17, brick: 22, base: 16.5, pitch: 60, family: SANS, kind: 'word', physics: true },
  small: { size: 14.5, brick: 19, base: 14, pitch: 54, family: SANS, kind: 'word', physics: true },
  info: { size: 15, brick: 20, base: 14.5, pitch: 54, family: SANS, kind: 'word', physics: true },
  label: { size: 14, brick: 19, base: 14, pitch: 54, family: SANS, weight: 600, color: '#54595d', kind: 'label', physics: false },
  title: { size: 40, brick: 44, base: 35, pitch: 96, family: SERIF, kind: 'title', physics: false },
  h2: { size: 29, brick: 34, base: 27.5, pitch: 84, family: SERIF, kind: 'heading', physics: false },
  h3: { size: 21, brick: 26, base: 20.5, pitch: 70, family: SANS, weight: 700, kind: 'heading', physics: false },
  h4: { size: 18, brick: 23, base: 17.5, pitch: 64, family: SANS, weight: 700, kind: 'heading', physics: false },
  ibTitle: { size: 21, brick: 26, base: 20.5, pitch: 62, family: SERIF, weight: 700, kind: 'heading', physics: false },
  ibHeader: { size: 15, brick: 20, base: 15, pitch: 54, family: SANS, weight: 700, kind: 'label', physics: false },
  toc: { size: 16, brick: 21, base: 16, pitch: 52, family: SANS, kind: 'door', physics: false },
  door: { size: 13.5, brick: 19, base: 14, pitch: 52, family: SANS, weight: 600, kind: 'door', physics: false },
  desc: { size: 15, brick: 20, base: 15, pitch: 56, family: SANS, italic: true, color: '#54595d', kind: 'word', physics: true },
  hatnote: { size: 15, brick: 20, base: 15, pitch: 56, family: SANS, italic: true, color: '#54595d', kind: 'word', physics: true },
} satisfies Record<string, TextStyle>;

function fontOf(st: TextStyle, t?: Tok): string {
  const italic = st.italic || (t?.italic ?? false);
  const weight = t?.bold && st.physics ? 700 : (st.weight ?? 400);
  return `${italic ? 'italic ' : ''}${weight} ${st.size}px ${st.family}`;
}

const frac = (v: number) => v - Math.floor(v);

export function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface Placed {
  t: Tok;
  x: number;
  w: number;
}

interface FlowOpts {
  /** Add a one-way gutter ledge at each line so walking left off a line doesn't drop you. */
  ledge?: boolean;
  center?: boolean;
  /** Guarantee a passable opening in every line (default true). */
  ensure?: boolean;
}

const plain = (text: string): Run[] => [{ text }];

class Builder {
  tiles: Tile[] = [];
  links = new Map<string, LinkInfo>();
  occMap = new Map<number, Occurrence>();
  coins: Coin[] = [];
  enemies: EnemySpawn[] = [];
  sections: Section[] = [];
  marks: Level['marks'] = [];
  rules: Level['rules'] = [];
  panels: Panel[] = [];
  decor: Decor[] = [];
  tocSpawn?: { x: number; y: number };
  words = 0;
  y = 0;
  section = 0;
  occCounter = { n: 0 };
  private throwaway = { n: 0 };

  constructor(private measure: Measure) {}

  toks(runs: Run[], links = true): Tok[] {
    return tokenize(runs, links ? this.occCounter : this.throwaway);
  }

  add(p: Omit<Tile, 'id' | 'section'>): Tile {
    const t: Tile = { ...p, id: this.tiles.length, section: this.section };
    this.tiles.push(t);
    return t;
  }

  private passable(line: Placed[]): boolean {
    const { left, ledgeEnd, wallR, minGap } = LAYOUT;
    const first = line[0];
    const last = line[line.length - 1];
    let best = Math.max(first.x <= left + 1 ? 0 : first.x - ledgeEnd, wallR - (last.x + last.w));
    for (let k = 1; k < line.length; k++) best = Math.max(best, line[k].x - (line[k - 1].x + line[k - 1].w));
    return best >= minGap;
  }

  private splitWord(t: Tok, maxW: number, font: string): Tok[] {
    const out: Tok[] = [];
    let cur = '';
    for (const ch of Array.from(t.text)) {
      if (cur && this.measure(cur + ch, font) > maxW) {
        out.push({ ...t, text: cur, joinNext: true, sentenceEnd: false, cites: [], cn: false });
        cur = '';
      }
      cur += ch;
    }
    out.push({ ...t, text: cur });
    return out;
  }

  /** Lay out words left-to-right in [x0, xMax], wrapping lines. Returns the y of the next line slot. */
  flow(input: Tok[], st: TextStyle, x0: number, xMax: number, y: number, opts: FlowOpts = {}): number {
    const toks = input.slice();
    let i = 0;
    let lineY = y;
    let line: Placed[] = [];
    let cursor = x0;
    const center = () => {
      const last = line[line.length - 1];
      const shift = Math.round((x0 + xMax) / 2 - (last.x + last.w - line[0].x) / 2 - line[0].x);
      for (const it of line) it.x += shift;
    };
    const commit = () => {
      if (!line.length) return;
      if (opts.center) center();
      if (opts.ensure !== false) {
        while (line.length > 1 && !this.passable(line)) {
          // Push trailing words (and anything glued to them) back so they re-flow onto the next line.
          do {
            line.pop();
            i--;
          } while (line.length > 1 && line[line.length - 1].t.joinNext);
          if (opts.center) center();
        }
      }
      this.emitLine(line, st, lineY, !!opts.ledge);
      line = [];
      cursor = x0;
      lineY += st.pitch;
    };
    // In a full-width column, a single word spanning the whole line would make it impassable; split such words earlier.
    const fullWidth = x0 - LAYOUT.ledgeEnd < LAYOUT.minGap && LAYOUT.wallR - xMax < LAYOUT.minGap;
    const maxWord = opts.ensure !== false && fullWidth ? xMax - x0 - LAYOUT.minGap : xMax - x0;
    // The final commit can push words back too, so keep flowing until every token is placed.
    while (i < toks.length) {
      while (i < toks.length) {
        const t = toks[i];
        const font = fontOf(st, t);
        const w = this.measure(t.text, font);
        if (w > maxWord && Array.from(t.text).length > 1) {
          toks.splice(i, 1, ...this.splitWord(t, maxWord, font));
          continue;
        }
        const prev = line.length ? toks[i - 1] : undefined;
        const gap = !prev || prev.joinNext ? 0 : prev.sentenceEnd ? LAYOUT.hole : this.measure(' ', font);
        if (line.length && cursor + gap + w > xMax) {
          commit();
          continue;
        }
        line.push({ t, x: cursor + gap, w });
        cursor += gap + w;
        i++;
      }
      commit();
    }
    return lineY;
  }

  private emitLine(line: Placed[], st: TextStyle, y: number, ledge: boolean): void {
    if (ledge)
      this.add({ kind: 'ledge', x: LAYOUT.ledgeX, y, w: LAYOUT.ledgeEnd - LAYOUT.ledgeX, h: 8, flags: F.ONEWAY });
    for (const it of line) {
      const t = it.t;
      let flags: number = F.SOLID;
      if (st.physics) {
        if (t.paren) flags = F.ONEWAY;
        if (t.number) flags |= F.TIMED;
        if (t.bold) flags |= F.SPRING;
        if (t.italic || st.italic) flags |= F.ICE;
      }
      const isLink = !!t.link && !t.red;
      if (t.link) flags |= t.red ? F.RED | F.HAZARD : F.LINK;
      const tile = this.add({
        kind: st.kind,
        x: it.x,
        y,
        w: it.w,
        h: st.brick,
        flags,
        text: t.text,
        font: fontOf(st, t),
        base: st.base,
        color: st.color,
        link: isLink ? t.link : undefined,
        occ: isLink ? t.occ : undefined,
      });
      if (flags & F.TIMED) tile.phase = frac(it.x * 0.0031 + y * 0.0047);
      if (isLink && t.occ !== undefined) this.addOcc(t, tile);
      t.cites.forEach((c, k) => this.coins.push({ x: it.x + it.w + 5 + k * 15, y: y - 21, label: c }));
      if (t.cn) this.enemies.push({ x: it.x + it.w - 7, y: y - 15, kind: 'cn' });
      if (st.kind === 'word') this.words++;
    }
  }

  private addOcc(t: Tok, tile: Tile): void {
    const id = t.occ!;
    const title = t.link!;
    let o = this.occMap.get(id);
    if (!o) {
      o = { id, title, tiles: [], section: this.section, text: '', x: tile.x, y: tile.y };
      this.occMap.set(id, o);
    }
    o.tiles.push(tile.id);
    o.text += (o.text && !o.text.endsWith('\u200b') ? ' ' : '') + t.text + (t.joinNext ? '\u200b' : '');
    let info = this.links.get(title);
    if (!info) {
      info = { title, redirect: !!t.redirect, occs: [], flags: 0 };
      this.links.set(title, info);
    }
    if (!info.occs.includes(id)) info.occs.push(id);
  }

  door(label: string, door: Tile['door'], x: number, y: number, align: 'left' | 'right' = 'left'): Tile {
    const font = fontOf(S.door);
    const w = this.measure(label, font) + 10;
    return this.add({
      kind: 'door',
      x: align === 'right' ? x - w : x,
      y,
      w,
      h: S.door.brick,
      flags: F.SOLID,
      text: label,
      font,
      base: S.door.base,
      door,
    });
  }

  para(b: Extract<Block, { kind: 'para' }>): void {
    const st = b.style === 'hatnote' ? S.hatnote : S.body;
    const x0 = LAYOUT.left + b.indent * LAYOUT.indent;
    const toks = this.toks(b.runs);
    if (!toks.length) return;
    const top = this.y;
    if (b.bullet)
      this.decor.push({ x: x0 - 9, y: top + st.base, text: b.bullet, font: fontOf(st), color: '#202122', align: 'right' });
    else this.marks.push({ x: LAYOUT.ladderX + LAYOUT.ladderW / 2, y: top, kind: 'para' });
    const next = this.flow(toks, st, x0, LAYOUT.right, top, { ledge: true });
    if (b.style === 'quote') this.panels.push({ x: x0 - 14, y: top - 16, w: 4, h: next - top - st.pitch + st.brick + 26, kind: 'quote' });
    this.y = next + (b.bullet ? 0 : LAYOUT.paraGap);
  }

  heading(level: number, text: string, section: number, hasToc: boolean): void {
    const st = level === 2 ? S.h2 : level === 3 ? S.h3 : S.h4;
    this.y += level === 2 ? 34 : 14;
    this.section = section;
    const y = this.y;
    const reserve = level === 2 && hasToc ? 150 : 0;
    const next = this.flow(this.toks(plain(text), false), st, LAYOUT.left, LAYOUT.right - reserve, y, { ledge: true });
    this.sections.push({ title: text, level, y, spawn: { x: LAYOUT.left + 8, y: y - 30 } });
    this.marks.push({ x: LAYOUT.ladderX + LAYOUT.ladderW / 2, y, kind: 'heading' });
    if (level === 2) {
      this.rules.push({ x: LAYOUT.left, y: next - st.pitch + st.brick + 9, w: LAYOUT.right - LAYOUT.left });
      if (hasToc) this.door('⇡ contents', { kind: 'contents' }, LAYOUT.right, y, 'right');
    }
    this.y = next;
  }

  figure(b: Extract<Block, { kind: 'figure' }>): void {
    const { image, align } = b;
    const s = Math.min(290 / image.width, 280 / image.height, 1.3);
    const iw = Math.round(image.width * s);
    const ih = Math.round(image.height * s);
    const ix =
      align === 'left' ? LAYOUT.left : align === 'center' ? Math.round((LAYOUT.left + LAYOUT.right - iw) / 2) : LAYOUT.right - iw;
    const y = this.y + 6;
    this.add({ kind: 'image', x: ix, y, w: iw, h: ih, flags: F.SOLID, image });
    const toks = this.toks(b.caption);
    if (toks.length) {
      const next = this.flow(toks, S.small, ix, ix + iw, y + ih + 40, { ledge: ix <= LAYOUT.left + 1 });
      this.y = next + LAYOUT.paraGap;
    } else this.y = y + ih + 44 + LAYOUT.paraGap;
  }

  infobox(b: Extract<Block, { kind: 'infobox' }>): void {
    const { left, right } = LAYOUT;
    const top = this.y;
    let y = top + 22;
    y = this.flow(this.toks(plain(b.title), false), S.ibTitle, left + 16, right - 16, y, { center: true, ledge: true });
    if (b.image) {
      const s = Math.min(300 / b.image.width, 250 / b.image.height, 1.3);
      const iw = Math.round(b.image.width * s);
      const ih = Math.round(b.image.height * s);
      this.add({ kind: 'image', x: Math.round((left + right - iw) / 2), y, w: iw, h: ih, flags: F.SOLID, image: b.image });
      y += ih + 40;
    }
    for (const row of b.rows) {
      if (row.header) {
        this.panels.push({ x: left + 4, y: y - 14, w: right - left - 8, h: S.ibHeader.brick + 22, kind: 'header' });
        y = this.flow(this.toks(plain(row.label), false), S.ibHeader, left + 16, right - 16, y, { center: true, ledge: true });
        continue;
      }
      const ly = this.flow(this.toks(plain(row.label), false), S.label, left + 16, left + 176, y, { ledge: true });
      const vy = this.flow(this.toks(row.value), S.info, left + 216, right - 16, y, { ensure: false });
      y = Math.max(ly, vy);
    }
    this.panels.push({ x: left - 10, y: top, w: right - left + 20, h: y - top - 16, kind: 'infobox' });
    this.y = y + LAYOUT.paraGap;
  }

  toc(entries: { num: number; text: string; section: number }[]): void {
    const { left, right } = LAYOUT;
    const top = this.y;
    let y = top + 22;
    this.tocSpawn = { x: left + 30, y: top - 28 };
    y = this.flow(this.toks(plain('Contents'), false), S.ibHeader, left + 16, right, y, { ledge: true });
    const font = fontOf(S.toc);
    for (const e of entries) {
      let label = `${e.num}   ${e.text}`;
      while (label.length > 8 && this.measure(label, font) > right - left - 120) label = `${label.slice(0, -2).trimEnd()}…`;
      this.add({ kind: 'ledge', x: LAYOUT.ledgeX, y, w: LAYOUT.ledgeEnd - LAYOUT.ledgeX, h: 8, flags: F.ONEWAY });
      this.add({
        kind: 'door',
        x: left + 26,
        y,
        w: this.measure(label, font) + 12,
        h: S.toc.brick,
        flags: F.SOLID,
        text: label,
        font,
        base: S.toc.base,
        door: { kind: 'toc', section: e.section },
      });
      y += S.toc.pitch;
    }
    this.panels.push({ x: left - 10, y: top, w: Math.min(right - left + 20, 520), h: y - top - 14, kind: 'toc' });
    this.y = y + LAYOUT.paraGap;
  }
}

export interface LayoutOptions {
  measure: Measure;
  hasBack?: boolean;
}

export function buildLevel(doc: ArticleDoc, opts: LayoutOptions): Level {
  const b = new Builder(opts.measure);
  const { left, right, wallR } = LAYOUT;

  // --- header: tagline, title, back door, short description
  b.decor.push({ x: left, y: 40, text: 'From Wikipedia, the free encyclopedia', font: `400 13px ${SANS}`, color: '#72777d' });
  const titleY = 70;
  let next = b.flow(b.toks(plain(doc.displayTitle), false), S.title, left, right - (opts.hasBack ? 130 : 0), titleY, {
    ledge: true,
  });
  if (opts.hasBack) b.door('⟲ back', { kind: 'back' }, right, titleY, 'right');
  b.sections.push({ title: doc.displayTitle, level: 1, y: titleY, spawn: { x: left + 14, y: titleY - 44 } });
  b.rules.push({ x: left, y: next - S.title.pitch + S.title.brick + 12, w: right - left });
  b.y = next;
  if (doc.description) b.y = b.flow(b.toks(plain(doc.description), false), S.desc, left, right, b.y, { ledge: true }) + 4;

  // --- lead: hatnotes, lead paragraphs, then the infobox as a fact box
  const firstHeading = doc.blocks.findIndex((x) => x.kind === 'heading');
  const lead = firstHeading < 0 ? doc.blocks : doc.blocks.slice(0, firstHeading);
  const rest = firstHeading < 0 ? [] : doc.blocks.slice(firstHeading);
  const infobox = doc.blocks.find((x) => x.kind === 'infobox');
  const emit = (blk: Block) => {
    if (blk.kind === 'para') b.para(blk);
    else if (blk.kind === 'figure') b.figure(blk);
  };
  for (const blk of lead) if (blk.kind === 'para' && blk.style === 'hatnote') emit(blk);
  for (const blk of lead) if (!(blk.kind === 'para' && blk.style === 'hatnote')) emit(blk);
  if (infobox && infobox.kind === 'infobox') b.infobox(infobox);

  // --- table of contents: H2 sections become teleport doors
  const toc: { num: number; text: string; section: number }[] = [];
  let sectionIdx = 0;
  for (const blk of rest) {
    if (blk.kind !== 'heading') continue;
    sectionIdx++;
    if (blk.level === 2) toc.push({ num: toc.length + 1, text: blk.text, section: sectionIdx });
  }
  const hasToc = toc.length >= 3;
  if (hasToc) b.toc(toc);

  // --- body sections
  sectionIdx = 0;
  for (const blk of rest) {
    if (blk.kind === 'heading') b.heading(blk.level, blk.text, ++sectionIdx, hasToc);
    else emit(blk);
  }

  // --- footer and page boundaries
  const floorY = b.y + 70;
  b.door('⇡ back to top', { kind: 'top' }, left + 6, floorY - S.door.brick);
  b.decor.push({ x: (left + right) / 2, y: floorY + 44, text: `End of “${doc.displayTitle}”`, font: `italic 400 14px ${SERIF}`, color: '#72777d', align: 'center' });
  b.decor.push({
    x: (left + right) / 2,
    y: floorY + 66,
    text: 'Text from Wikipedia by its contributors, CC BY-SA 4.0',
    font: `400 11px ${SANS}`,
    color: '#8a8f96',
    align: 'center',
  });
  const height = floorY + 140;
  b.section = Math.max(0, b.sections.length - 1);
  b.add({ kind: 'wall', x: -400, y: floorY, w: wallR + 800, h: 400, flags: F.SOLID });
  b.add({ kind: 'wall', x: -400, y: -480, w: 400, h: height + 880, flags: F.SOLID });
  b.add({ kind: 'wall', x: wallR, y: -480, w: 400, h: height + 880, flags: F.SOLID });
  b.add({ kind: 'wall', x: -400, y: -480, w: wallR + 800, h: 400, flags: F.SOLID });

  // --- a few roaming vandals, deterministic per article
  const rng = mulberry32(hashString(doc.title));
  const candidates = b.tiles.filter(
    (t) => t.kind === 'word' && t.flags === F.SOLID && t.y > 500 && t.w >= 34,
  );
  const vandals = Math.min(14, Math.floor(b.words / 480));
  for (let k = 0; k < vandals && candidates.length; k++) {
    const t = candidates[Math.floor(rng() * candidates.length)];
    b.enemies.push({ x: t.x + t.w / 2 - 7, y: t.y - 15, kind: 'vandal' });
  }

  // --- densify occurrence ids
  const remap = new Map<number, number>();
  const occs: Occurrence[] = [];
  for (const o of [...b.occMap.values()].sort((a, z) => a.id - z.id)) {
    remap.set(o.id, occs.length);
    o.text = o.text.replace(/\u200b/g, '');
    o.id = occs.length;
    occs.push(o);
  }
  for (const t of b.tiles) if (t.occ !== undefined) t.occ = remap.get(t.occ);
  for (const info of b.links.values()) info.occs = info.occs.map((id) => remap.get(id)!).filter((x) => x !== undefined);

  return {
    title: doc.title,
    displayTitle: doc.displayTitle,
    description: doc.description,
    lang: doc.lang,
    width: LAYOUT.pageW,
    height,
    tiles: b.tiles,
    links: b.links,
    occs,
    coins: b.coins,
    enemies: b.enemies,
    sections: b.sections,
    spawn: b.sections[0].spawn,
    tocSpawn: b.tocSpawn,
    ladder: { x: LAYOUT.ladderX, w: LAYOUT.ladderW, y0: titleY - 10, y1: floorY },
    marks: b.marks,
    rules: b.rules,
    panels: b.panels,
    decor: b.decor,
    words: b.words,
  };
}

let measureCtx: CanvasRenderingContext2D | null = null;
let measureFont = '';
const measureCache = new Map<string, number>();

/** Canvas-backed text measurement with a cache (browser only). */
export const canvasMeasure: Measure = (text, font) => {
  const key = `${font}\u0000${text}`;
  const hit = measureCache.get(key);
  if (hit !== undefined) return hit;
  measureCtx ??= document.createElement('canvas').getContext('2d');
  if (!measureCtx) return text.length * 8.5;
  if (measureFont !== font) {
    measureCtx.font = font;
    measureFont = font;
  }
  const w = measureCtx.measureText(text).width;
  if (measureCache.size > 60000) measureCache.clear();
  measureCache.set(key, w);
  return w;
};

/** Deterministic approximate measurement for tests / non-canvas environments. */
export const approxMeasure: Measure = (text, font) => {
  const size = Number(/([\d.]+)px/.exec(font)?.[1] ?? 16);
  return Array.from(text).length * size * 0.52;
};
